'use client';

import Button from '@/components/Button';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './Heading.module.css';

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'p' | 'span';

interface BaseHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  textCentered?: boolean;
  textClassName?: string;
  subtitle?: string;
}

type ConditionalProps = | {
  asBack?: false | undefined;
  onBack?: never
} | {
  asBack: true;
  onBack?: () => void;
};


export interface HeadingProps<T extends HeadingElement> extends BaseHeadingProps {
  as?: T;
  action?: React.ReactNode;
  sticky?: boolean;
}

type PolymorphicComponentProps<T extends HeadingElement> = ConditionalProps & React.PropsWithoutRef<React.ComponentProps<T>> & HeadingProps<T>;

function HeadingInner<T extends HeadingElement = 'h2'>(
  props: PolymorphicComponentProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const { as: Component = 'h2', children, subtitle, className, textClassName, textCentered = false, icon, action, sticky = false, asBack = false, onBack, ...rest } = props;
  const router = useRouter();

  return (
    <div className={twMerge('flex items-center gap-4 py-1', Component === 'h1' && 'mb-4' , asBack ? 'px-0' : 'px-4', sticky ? styles.sticked : '', !asBack && !action && 'w-full justify-normal', className)}>
      {asBack && (
        <Button type='IconButton' className='bg-btn-primary !size-10 flex-shrink-0' onClick={() => (onBack ? onBack() : router.back())}>
          <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3 text-white" />
        </Button>
      )}
      {icon && (
        <div className="flex-shrink-0">
          {icon}
        </div>
      )}
      <Component 
        ref={ref as any} 
        className={twMerge('flex-1 flex flex-col justify-center', styles[Component], textClassName, textCentered ? 'items-center' : 'items-start', (asBack || action) && 'flex-grow', subtitle && '!leading-[1.1]')}
        {...rest as any} 
      >
        {children}
        {subtitle && <small className='!text-sm !leading-[1]'>{subtitle}</small>}
      </Component>
      {action && <div className="flex-shrink-0 self-end">
        {action}
      </div>}
    </div>
  );
}

const HeadingForwardRef = React.forwardRef(HeadingInner);

// TypeScript compatibility: Generic polymorphic components with forwardRef have type inference issues in React 18
// Usage sites should use @ts-expect-error to work around this limitation
const Heading = HeadingForwardRef as <T extends HeadingElement = 'h2'>(
  props: PolymorphicComponentProps<T> & { ref?: React.Ref<HTMLElement> }
) => React.ReactElement;

// @ts-ignore
Heading.displayName = 'Heading';

export default Heading;